import {
  SearchV2Params,
  useDiscoverySearchV2,
} from '@teko/discovery/dist/v2/useDiscoverySearch';
import { HAMPER_PAGE_SIZE } from 'constants/index';
import { useCartV2, useFrame, useServices, useTenantConfig } from 'contexts';
import { IS_OVER_18_KEY } from 'helpers';
import { useEffect, useMemo, useState } from 'react';
import { useHandlerUnderEighteenYearOld } from './useHandlerUnderEighteenYearOld';

export const useHamperProducts = (
  params: SearchV2Params,
  hamperItemKey?: string,
) => {
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { isDisplayUom, showNewProductGift } = useTenantConfig();
  const { frames } = useFrame();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const cartV2 = useCartV2();
  const [isOver18Confirmed, setIsOver18Confirmed] = useState(() =>
    typeof window !== 'undefined'
      ? localStorage.getItem(IS_OVER_18_KEY) === 'true'
      : false,
  );

  // Listen to localStorage changes to trigger re-render hamper cards when user confirms 18+
  useEffect(() => {
    const handleStorageChange = () => {
      const isOver18 = localStorage.getItem(IS_OVER_18_KEY) === 'true';
      setIsOver18Confirmed(isOver18);
    };

    window.addEventListener('storage', handleStorageChange);

    // Sync changes for current tab (since storage event only fires for other tabs)
    const interval = setInterval(() => {
      const currentValue = localStorage.getItem(IS_OVER_18_KEY) === 'true';
      if (currentValue !== isOver18Confirmed) {
        setIsOver18Confirmed(currentValue);
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isOver18Confirmed]);

  const { result, ...rest } = useDiscoverySearchV2(
    discoveryServiceV2,
    {
      ...params,
      isNeedFeaturedProducts: false,
    },
    {
      transform: (data) => ({
        products: data.products?.map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
            showUom: isDisplayUom,
            frames,
            showNewProductGift,
            cartV2,
          }),
        ),
        filter: data.filter,
        pagination: {
          totalItems: data.total,
          totalPages: Math.ceil(data.total / HAMPER_PAGE_SIZE),
        },
      }),
    },
  );

  const products = useMemo(() => result?.products || [], [result]);

  const productList = useMemo(() => {
    if (!products.length || !hamperItemKey) return [];

    return products.map(transformProductWhenUserUnder18);
    // eslint-disable-next-line
  }, [
    products,
    transformProductWhenUserUnder18,
    hamperItemKey,
    isOver18Confirmed,
  ]);

  return {
    products: productList,
    filter: result?.filter,
    pagination: result?.pagination,
    ...rest,
  };
};
